import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/shared/ui/layout";
import { cn } from "@/shared/lib/cn";
import { AdminGuard, DataTableFrame } from "@/features/admin/components";
import { AuditMovementsTab, EMPTY_MOVEMENT_FILTERS, type MovementFilters } from "./AuditMovementsTab";
import { AuditErrorsTab } from "./AuditErrorsTab";
import { AuditBlocksTab } from "./AuditBlocksTab";

/**
 * ═══════════════════════════════════════════════════════════════════
 * Bitácora (Anahí, 2-oct-2026): «una bitácora con sus peticiones, quién
 * realizó su petición y la respuesta de dicha petición», como la de
 * Matrix. Una card con tres pestañas —el mismo marco de Franquicias—:
 *
 * · Movimientos: todo lo que cambió algo, todo error y los reventones
 *   del navegador, con la petición y la respuesta completas al abrirlo.
 * · Errores: lo que se está rompiendo, agrupado, y a cuántas personas.
 *   Un grupo lleva a Movimientos ya filtrado por su ruta.
 * · Lista negra: las IP vetadas, con el botón para levantar un veto que
 *   cayó sobre alguien de verdad.
 * ═══════════════════════════════════════════════════════════════════
 */
export function AdminAuditPage() {
  return (
    <AdminGuard>
      <AuditScreen />
    </AdminGuard>
  );
}

const TABS = [
  { key: "movimientos" as const, label: "Movimientos" },
  { key: "errores" as const, label: "Errores" },
  { key: "lista-negra" as const, label: "Lista negra" },
];

type TabKey = (typeof TABS)[number]["key"];

function AuditScreen() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<TabKey>("movimientos");
  /*
   * Los filtros de Movimientos viven AQUÍ y no dentro de su pestaña: un
   * grupo de Errores salta a Movimientos con la ruta y «error» ya puestos,
   * y al volver de otra pestaña los filtros siguen donde se dejaron.
   */
  const [filters, setFilters] = useState<MovementFilters>(EMPTY_MOVEMENT_FILTERS);
  const [page, setPage] = useState(1);

  function verMovimientosDe(path: string) {
    setFilters({ ...EMPTY_MOVEMENT_FILTERS, q: path, outcome: "error" });
    setPage(1);
    setTab("movimientos");
  }

  return (
    <>
      <PageHeader
        titleHidden
        title={t("Bitácora")}
        description={t("Quién pidió qué, qué se le contestó y qué IP están vetadas.")}
      />

      <DataTableFrame>
        {/* ── Las pestañas, integradas al borde superior ──────────────── */}
        <div
          role="tablist"
          aria-label={t("Bitácora")}
          className="flex flex-wrap border-b border-ds-border bg-ds-cream-2/50"
        >
          {TABS.map((item) => (
            <button
              key={item.key}
              type="button"
              role="tab"
              aria-selected={tab === item.key}
              onClick={() => setTab(item.key)}
              className={cn(
                "relative inline-flex items-center px-5 py-3.5 text-[13px] font-semibold transition",
                tab === item.key
                  ? "bg-white text-ds-violet-pop after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:bg-ds-violet-pop"
                  : "text-ds-ink-3 hover:bg-white/60 hover:text-ds-ink",
              )}
            >
              {t(item.label)}
            </button>
          ))}
        </div>

        {tab === "movimientos" && (
          <AuditMovementsTab
            filters={filters}
            onFiltersChange={(next) => {
              setFilters(next);
              setPage(1);
            }}
            page={page}
            onPageChange={setPage}
          />
        )}
        {tab === "errores" && <AuditErrorsTab onOpenGroup={verMovimientosDe} />}
        {tab === "lista-negra" && <AuditBlocksTab />}
      </DataTableFrame>
    </>
  );
}
